import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { Service } from 'rainbow-web-sdk/services/service';
import * as v from 'valibot';

export const URL_METADATA_SVC = 'UrlMetadataService';

const UrlMetadataAPIResponseSchema = v.object({
    title: v.pipe(v.string(), v.minLength(1)),
    description: v.optional(v.string()),
    url: v.pipe(v.string(), v.url()),
    urlOnly: v.fallback(
        v.pipe(v.string(), v.transform(val => val === 'true')),
        false
    ),
    image: v.fallback(
        v.optional(
            v.pipe(v.string(), v.url(), v.maxLength(1500)),
        ),
        undefined
    )
});

type UrlMetadataAPIResponse = v.InferInput<typeof UrlMetadataAPIResponseSchema>;
type UrlMetadataData = v.InferOutput<typeof UrlMetadataAPIResponseSchema>;

export class UrlMetaData {

    public title: string;
    public url: string;
    public description?: string;
    public image?: string;
    public urlOnly: boolean = false;

    private constructor(data: UrlMetadataData) {
        this.url = data.url;
        this.title = data.title;
        this.description = data.description;
        this.urlOnly = data.urlOnly;

        if (data.image) {
            if (data.image.startsWith('/')) {
                this.image = this.url + data.image;
            } else if (data.image.startsWith('./') || data.image.startsWith('../')) {
                this.image = `${this.url}/${data.image}`;
            } else {
                this.image = data.image;
            }
        }
    }

    public static create(data: UrlMetadataAPIResponse): UrlMetaData | null {
        const result = v.safeParse(UrlMetadataAPIResponseSchema, data);
        if (!result.success) return null;

        return new UrlMetaData(result.output);
    }
}

export interface IUrlMetadataService {
    getUrlFromString(text: string): string | undefined;
    getUrlMetadata(url: string, timeout: number): Promise<UrlMetaData | null>;
}

/**
 * @module
 * @name UrlMetadataService
 * @public
 * @description This service manages urlMetadata in Rainbow.
 */
export class UrlMetadataService extends Service implements IUrlMetadataService {

    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private linkRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig;
    private abortController: AbortController;

    /**
     * @function getInstance
     * @static
     * @private
     * @description return the urlMetaSErvice singleton
     * @returns {UrlMetadataService} the UrlMetadataService instance
     */
    public static getInstance() {
        return Service.create(URL_METADATA_SVC, UrlMetadataService);
    }

    public static build() {
        return new UrlMetadataService();
    }

    private constructor() { super(); }

    public getUrlFromString(text: string): string | undefined {
        const urls = text.match(this.linkRegex);
        return urls ? urls[0] : undefined;
    }

    public async getUrlMetadata(url: string, timeout: number = 60000): Promise<UrlMetaData | null> {
        try {
            this.abortController?.abort();
            this.abortController = new AbortController();

            const combinedSignal = AbortSignal.any([
                this.abortController.signal,
                AbortSignal.timeout(timeout)
            ]);

            const serviceUrl = `${config.restServerUrl}/api/rainbow/urlmetadata/v1.0/url`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(serviceUrl, {
                method: 'POST', headers,
                body: JSON.stringify({ url }),
                signal: combinedSignal
            });
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            this.abortController = undefined;
            this.logger.info(`[urlMetadataService] getUrlMetadata -- success`);
            return UrlMetaData.create(data);
        }
        catch (error) {
            this.abortController = undefined;
            if (error instanceof Error) {
                if (error.name === 'TimeoutError') {
                    this.logger.info(`[urlMetadataService] getUrlMetadata -- timed out`);
                } else if (error.name === 'AbortError') {
                    this.logger.info(`[urlMetadataService] getUrlMetadata -- aborted`);
                }
            } else {
                this.logger.info(`[urlMetadataService] getUrlMetadata -- failure`);
            }
            return null;
        }
    }

    public abortUrlMetadataRequest(): void {
        this.logger.info(`[urlMetadataService] abortUrlMetadataRequest`);
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }
    }
}
